import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import StopScreenShareIcon from '@material-ui/icons/StopScreenShare';
import clsx from 'clsx';
import noVideoImage from 'images/videocam-24px.svg';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import VideoTileControls from './VideoTileControls';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    backgroundColor: 'gray',
    border: '6px solid white',
    height: (props) => (props.height ? props.height : '33vh'),
    width: '100%',
  },
  video: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: '0px',
    left: '0px',
    objectFit: 'cover',
    backgroundImage: `url(${noVideoImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  },
  overlay: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    fontSize: '20px',
    zIndex: 1000,
  },
  hide: {
    display: 'none',
  },
  border: {
    border: '6px solid #05C4FF',
  },
  contentIcon: {
    color: 'rgba(255,0,0,1)',
    padding: '.2em',
    '&:hover': {
      color: 'rgba(255,0,0,.6)',
      cursor: 'pointer',
    },
  },
  contentBackground: {
    display: 'flex',
    color: 'white',
    padding: theme.spacing(1),
  },
  avatar: {
    marginLeft: theme.spacing(1),
    cursor: 'pointer',
  },
  localChip: {
    backgroundColor: theme.palette.primary.main,
    height: theme.spacing(3),
    marginRight: theme.spacing(3),
    color: 'white',
    fontWeight: '600',
  },
  chip: {
    backgroundColor: '#0000008A',
    height: theme.spacing(3),
    marginRight: theme.spacing(3),
    color: 'white',
    fontWeight: '600',
  },
}));

RemoteVideo.propTypes = {
  height: PropTypes.string,
  videoTileId: PropTypes.number,
  thisAttendeeId: PropTypes.string.isRequired,
  selectSpeaker: PropTypes.func,
  isSelected: PropTypes.bool,
  isContent: PropTypes.bool,
};

export default function RemoteVideo(props) {
  const { videoTileId, thisAttendeeId, selectSpeaker, isSelected, isContent } = props;
  const classes = useStyles(props);
  const videoElement = useRef(null);
  let contentName;
  const { attendeeId, audioVideo, activeSpeakers, roster } = useSelector(({ chime }) => ({
    attendeeId: chime.attendeeId,
    audioVideo: chime.audioVideo,
    activeSpeakers: chime.activeSpeakers,
    roster: chime.roster,
  }));

  useEffect(() => {
    audioVideo.bindVideoElement(videoTileId, videoElement.current);
    return () => {
      audioVideo.unbindVideoElement(videoTileId);
    };
    // eslint-disable-next-line
  }, [videoTileId]);

  const rootStyle = clsx({
    [classes.root]: true,
    [classes.border]: roster[thisAttendeeId]?.muted
      ? false
      : activeSpeakers.includes(thisAttendeeId),
  });

  const stopContentShareAction = () => {
    audioVideo.stopContentShare();
  };

  if (isContent) {
    Object.keys(roster).forEach((id) => {
      if (thisAttendeeId === `${id}#content`) {
        contentName = roster[id].name;
      }
    });
  }

  return (
    <div data-testid="video-tile-root" className={rootStyle}>
      <div className={classes.overlay}>
        {isContent && (
          <div className={classes.contentBackground}>
            {thisAttendeeId === `${attendeeId}#content` && (
              <Avatar className={classes.avatar} onClick={stopContentShareAction}>
                <StopScreenShareIcon fontSize="large" className={classes.contentIcon} />
              </Avatar>
            )}
            <Box flexGrow={1} />
            {thisAttendeeId === `${attendeeId}#content` ? (
              <Chip className={classes.localChip} label={'You are sharing your screen'} />
            ) : (
              <Chip className={classes.chip} label={`Watching: ${contentName}`} />
            )}
          </div>
        )}
        {!isContent && (
          <VideoTileControls
            thisAttendeeId={thisAttendeeId}
            isSelected={isSelected}
            selectSpeaker={selectSpeaker}
          />
        )}
      </div>
      <video data-testid="video-tile" className={classes.video} ref={videoElement} />
    </div>
  );
}
